import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ReviewProductDropdownField, ReviewTypeDropdownField, ReviewVendorDropdownField } from '@/Components/Dropdowns';
import { Validations, Action } from '@samc/centricity-core';
import { IReviewProductGroup } from './IReviewProductGroup';
import { ReviewProductFormModal, ReviewTypeFormModal, ReviewTypeVendorFormModal } from '@/Components/Modals';
import { AjaxHelper, errToMessage } from '@samc/common';
import { IReviewTypeVendor, IReviewProductRequest, IProductReviewType, IVendorReviewType, IReviewType } from '../../Interfaces';
import { ReviewProductSource, ReviewVendorSource, ReviewTypeSource } from '@/Sources';

@Component({
  name: 'review-product-selector',
  components: { ReviewProductDropdownField, ReviewTypeDropdownField, ReviewVendorDropdownField, Action }
})
export default class ReviewProductSelectorComponent extends Vue {
  private dataSourceUrl = '/datasource';
  @Prop({ type: Boolean, default: false }) public addReviewType!: boolean;
  @Prop({ type: Boolean, default: false }) public addReviewTypeVendor!: boolean;
  @Prop({ type: Boolean, default: false }) public addReviewProduct!: boolean;

  @Prop({ default: () => null }) public onlyImportProcess!: boolean | null;
  @Prop({ default: () => null }) public isException!: boolean | null;
  @Prop({ required: true }) public verificationToken!: string;
  @Prop() public value!: IReviewProductGroup;
  @Prop({ default: () => null }) public validations!: Validations<IReviewProductGroup> | null;
  @Prop() public fieldClass!: string;
  @Prop() public actionClass!: string;
  @Prop({ default: 'pb-2' }) public fieldWrapperClass!: string;
  @Prop({ type: Boolean, default: false }) public inline!: boolean;
  @Prop({ default: '' }) public indent!: string;
  @Prop({ type: Boolean, default: false }) public clearable!: boolean;
  @Prop({ type: Boolean, default: false }) public clearableReviewType!: boolean;
  @Prop({ type: Boolean, default: false }) public clearableReviewVendor!: boolean;
  @Prop({ type: Boolean, default: false }) public clearableReviewProduct!: boolean;
  @Prop({ default: '' }) public requiredFields!: string;

  @Prop({ type: Boolean, default: false }) public disabled!: boolean;
  @Prop({ type: Boolean, default: false }) public disableReviewType!: boolean;
  @Prop({ type: Boolean, default: false }) public disableReviewVendor!: boolean;
  @Prop({ type: Boolean, default: false }) public disableReviewProduct!: boolean;

  public model: IReviewProductGroup = { reviewTypeId: null, vendorId: null, productId: null };
  public get disableVendor(): boolean { return this.model.reviewTypeId === null || this.model.reviewTypeId.length === 0; }
  public get disableProduct(): boolean { return this.disableVendor || this.model.vendorId === null || this.model.vendorId.length === 0; }

  public get reviewTypeLabel(): string { return this.requiredFields.indexOf('review-type') > -1 ? 'Review Type *' : 'Review Type'; }
  public get reviewVendorLabel(): string { return this.requiredFields.indexOf('vendor') > -1 ? `${this.vendorName} *` : this.vendorName; }
  public get reviewProductLabel(): string { return this.requiredFields.indexOf('product') > -1 ? 'Product *' : 'Product'; }

  public get vendorName(): string { return this.reviewType === null || this.reviewType.name.toLowerCase().indexOf('custodian') === -1 ? 'Vendor' : 'Custodian'; }

  public productSource: ReviewProductSource | IProductReviewType[] = [];
  public vendorSource: ReviewVendorSource | IVendorReviewType[] = [];
  public reviewTypeSource: ReviewTypeSource | IReviewType[] = [];

  public reviewType: IReviewType | null = null;

  public helper: AjaxHelper | null = null;

  public async beforeUpdate() {
    // the prop value "verificationToken" is not available until the beforeUpdate lifecycle hook
    if (this.verificationToken.length == 0)
      this.verificationToken = await this.$verificationToken();
    this.helper = new AjaxHelper(this.dataSourceUrl, this.verificationToken, null, null);
  }

  //#region Add New Items
  public async openAddReviewTypeModal() {
    let newReviewType = await this.$modal<IReviewType | undefined>({ component: ReviewTypeFormModal, title: 'Add New Review Type', width: 450 });
    if (this.helper !== null && this.$user !== null && newReviewType !== undefined) {
      try {
        this.$loading('Creating Review Type');
        newReviewType.modifiedBy = this.$user.name;
        const reviewType = await this.helper.postData<IReviewType>('reviewtypes', newReviewType);

        if (!Array.isArray(this.reviewTypeSource)) {
          this.reviewTypeSource.clientItems.unshift(reviewType);
          this.reviewTypeSource.addItem(reviewType);
        }
        this.model.reviewTypeId = reviewType.id ?? null;
      } catch (err: any) {
        this.$log('Failed Adding New Review Type', err);
        this.$alert({
          type: 'error',
          title: 'Failed Adding New Review Type',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
      }
    }
  }

  public async openAddReviewTypeVendorModal() {
    let newReviewTypeVendor = await this.$modal<IReviewTypeVendor | undefined>({ component: ReviewTypeVendorFormModal, props: { verificationToken: this.verificationToken }, title: 'Add New Vendor', width: 450 });
    if (this.helper !== null && this.$user !== null && this.model.reviewTypeId !== null && newReviewTypeVendor !== undefined) {
      try {
        this.$loading(`Creating ${this.vendorName}`);
        newReviewTypeVendor.modifiedBy = this.$user.name;
        newReviewTypeVendor.reviewTypeId = this.model.reviewTypeId;
        const reviewTypeVendor = await this.helper.postData<IVendorReviewType>('reviewvendors', newReviewTypeVendor);

        if (!Array.isArray(this.vendorSource)) {
          this.vendorSource.clientItems.unshift(reviewTypeVendor);
          this.vendorSource.addItem(reviewTypeVendor);
        }

        // TODO: Need to double check that this id is the correct id
        this.model.vendorId = reviewTypeVendor.organizationId;
      } catch (err: any) {
        this.$log(`Failed Adding New ${this.vendorName}`, err);
        this.$alert({
          type: 'error',
          title: `Failed Adding New ${this.vendorName}`,
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
      }
    }
  }

  public async openAddReviewProductModal() {
    let newProduct = await this.$modal<IReviewProductRequest | undefined>({ component: ReviewProductFormModal, title: 'Add New Product', width: 450, props: { reviewTypeId: this.model.reviewTypeId } });
    if (this.helper !== null && this.model.reviewTypeId !== null && this.model.vendorId !== null && this.$user !== null && newProduct !== undefined) {
      try {
        this.$loading('Creating Product');
        newProduct.modifiedBy = this.$user.name;
        newProduct.reviewTypeId = this.model.reviewTypeId;
        newProduct.serviceVendorId = this.model.vendorId;
        const product = await this.helper.postData<IProductReviewType>('reviewproducts', newProduct);

        // TODO: Extract Review type data source
        if (!Array.isArray(this.productSource)) {
          this.productSource.clientItems.unshift(product);
          this.productSource.addItem(product);
        }
        this.model.productId = product.productId;
      } catch (err: any) {
        this.$log('Failed Adding New Product', err);
        this.$alert({
          type: 'error',
          title: 'Failed Adding New Product',
          errors: errToMessage(err),
          closingOption: 'holdOpen'
        });
      } finally {
        this.$loading();
      }
    }
  }
  //#endregion Add New Items

  public setReviewType(reviewType: IReviewType | null) {
    this.reviewType = reviewType;
    this.$emit('review-type', reviewType);
  }

  @Watch('value', { immediate: true, deep: true })
  public onValueChange(newValue?: IReviewProductGroup) {
    if (newValue !== undefined) {
      this.model = newValue;
    }
  }

  @Watch('model.reviewTypeId')
  public onReviewTypeIdChange(newReviewTypeId: string) {
    this.$emit('review-type-id', newReviewTypeId);
    this.$emit('input', this.model);
  }

  @Watch('model.vendorId')
  public onServiceVendorIdChange(newVendorId: string) {
    this.$emit('service-vendor-id', newVendorId);
    this.$emit('input', this.model);
  }

  @Watch('model.productId')
  public onReviewProductIdChange(newProductId: string) {
    this.$emit('review-product-id', newProductId);
    this.$emit('input', this.model);
  }
}